import React, { memo, useCallback } from 'react';
import { connect } from 'react-redux';
import { List } from '@mui/material';
import { createActions } from '../../../../store';
import * as PIXI from 'pixi.js';
import CollapsibleSwitchListItem from '../../../../../../list-items/CollapsibleSwitchListItem';
import CreatePageConstants, { DEFAULT_LAYER_GLOW_SETTINGS } from '../../../../CreatePageConstants';
import ToggleButtonListItem from '../../../../../../list-items/ToggleButtonListItem';
import SliderListItem from '../../../../../../list-items/SliderListItem';
import CondensedListItem from '../../../../../../list-items/CondensedListItem';
import { GLOW_TYPE } from '../../../../Preview/PreviewDisplayConstants';
import { previewHistoryBatch } from '../../../../store/extensions';

const glowTypesOptions = [
    { value: GLOW_TYPE.OUTER, text: 'Outer' },
    { value: GLOW_TYPE.INNER, text: 'Inner' },
];

const WaveLayerGlowListItem = memo(({
    index,
    enabled,
    glowSettings,
    updateWaveLayer,
    maxGlowBlur,
    maxGlowScale
}) => {
    const { glowType, blur, scale } = glowSettings;

    const onGlowEnabledChange = useCallback((value) => {
        updateWaveLayer(index, { glowSettings: { enabled: value } });
    }, [updateWaveLayer, index]);

    const onGlowTypeChange = useCallback((value) => {
        updateWaveLayer(index, { glowSettings: { glowType: value } });
    }, [updateWaveLayer, index]);

    const onGlowBlurChange = useCallback((value) => {
        previewHistoryBatch.start();
        updateWaveLayer(index, { glowSettings: { blur: value } });
    }, [updateWaveLayer, index]);

    const onGlowScaleChange = useCallback((value) => {
        previewHistoryBatch.start();
        updateWaveLayer(index, { glowSettings: { scale: value } });
    }, [updateWaveLayer, index]);

    return (
        <CollapsibleSwitchListItem
            checked={enabled}
            text='Glow'
            onChange={onGlowEnabledChange}
            warning={PIXI.utils.isWebGLSupported() ? null : CreatePageConstants.LIMITED_VISIBILITY_WARNING_TEXT}
            dense={true}
        >
            <List dense disablePadding>
                <ToggleButtonListItem
                    text='Type'
                    value={glowType}
                    onChange={onGlowTypeChange}
                    options={glowTypesOptions}
                    toolTip={
                        <>
                            <p>OUTER: The glow appears around the outside of the visualizer.</p>
                            <p>INNER: The glow appears inside the visualizer. This can cause visualizer colors to appear brighter and bleed into each other.</p>
                        </>
                    }
                />

                <CondensedListItem
                    elements={[
                        {
                            width: 6,
                            component: (
                                <SliderListItem
                                    title='Blur'
                                    value={blur}
                                    displayMultiplier={100 / maxGlowBlur}
                                    min={0}
                                    max={100}
                                    step={1}
                                    onValueChange={onGlowBlurChange}
                                />
                            )
                        },
                        {
                            width: 6,
                            component: (
                                <SliderListItem
                                    title='Scale'
                                    value={scale}
                                    displayMultiplier={100 / maxGlowScale}
                                    min={0}
                                    max={100}
                                    step={1}
                                    onValueChange={onGlowScaleChange}
                                />
                            )
                        }
                    ]}
                />
            </List>
        </CollapsibleSwitchListItem>
    );
});

const mapStateToProps = (state, { index }) => {
    const layer = state.createPreview.present.videoSettings.visualizer.waveCircles[index];
    const globalSettings = state.createPreview.present.videoSettings.visualizer.glowSettings;
    const layerGlowSettings = layer.customOptions?.glowSettings || DEFAULT_LAYER_GLOW_SETTINGS;

    return {
        enabled: layerGlowSettings.enabled,
        glowSettings: layerGlowSettings.enabled ? layerGlowSettings : globalSettings,
        maxGlowBlur: state.create.maxValues.maxGlowBlur,
        maxGlowScale: state.create.maxValues.maxGlowScale
    };
};

const mapDispatchToProps = {
    updateWaveLayer: (index, properties) => createActions.updateWaveLayer(index, properties)
};

export default connect(mapStateToProps, mapDispatchToProps)(WaveLayerGlowListItem);
